import React, { createRef, forwardRef, useCallback, useEffect, useState, } from 'react';
import styled, { css } from 'styled-components';
import { NumericFormat } from 'react-number-format';
import { Icon } from '../Icon';
import { Dropdown } from '../Dropdown';
import { getCurrencyFormat, isCurrencySymbolBefore } from '../../data/Currencies';
import { InputContainer, InputLoading, InputLoadingWrapper, InputWrapper } from './Input';
const AmountContainer = styled(InputContainer) `
  display: flex;
  overflow: clip;
`;
const Label = styled.div `
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.background};
`;
const CurrencyIconWrapper = styled.div `
  display: flex;
  align-items: center;

  svg {
    --icon-size: 16px;
    --icon-color: currentColor;
  }
`;
const Up = styled(Icon.ChevronUpLarge) `
  --icon-size: 14px;
  --icon-color: currentColor;

  pointer-events: none;
`;
const Down = styled(Icon.ChevronDownLarge) `
  --icon-size: 14px;
  --icon-color: currentColor;

  pointer-events: none;
`;
const StyledDropdown = styled(Dropdown) `
  --dropdown-custom-padding: 0;
`;
const InputElement = styled(NumericFormat) `
  appearance: none;
  background: none;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  color: inherit;
  letter-spacing: inherit;
  line-height: var(--input-line-height);
  height: var(--input-line-height);
  overflow: clip;
  display: block;
  width: 100%;
  transition: opacity 0.2s;

  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    transition:
      background-color 0s 9999s,
      color 0s 9999s;
  }

  &::placeholder {
    color: var(--input-placeholder-color);
    transition: color 0.2s;
  }

  ${({ $isLoading }) => $isLoading &&
    css `
      opacity: 0;
      pointer-events: none;
    `}
`;
export const AmountInput = forwardRef((props, ref) => {
    const [isLoadingState, setIsLoadingState] = useState(props.isLoading ?? false);
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const [currency, setCurrency] = useState(props.currencyList[0]);
    const [currencyOpen, setCurrencyOpen] = useState(false);
    const inputRef = createRef();
    const styleProps = {
        isHovered: props.isHovered ?? isHoveredState,
        isFocussed: props.isFocussed ?? isFocussedState,
        isFilled: props.isFilled ?? isFilledState,
        isDisabled: props.isDisabled ?? false,
        isLoading: props.isLoading ?? false,
        isPressed: props.isPressed ?? false,
        hasError: props.hasError ?? false,
        size: props.size ?? 'default',
        variant: props.variant ?? 'default',
    };
    const handleClick = () => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const handleHoverChange = (value) => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    const handleValueChange = useCallback((value) => {
        if (props.onChange) {
            props.onChange(value && value > 0 ? Math.round(value * currency.numberToBasic) : undefined);
        }
    }, [props.onChange, currency]);
    const handleCurrencyChange = useCallback((value) => {
        const newCurrency = props.currencyList.find(({ code }) => code === value);
        if (!newCurrency) {
            return;
        }
        const oldValue = props.value ?? 0;
        const newValue = Math.round((oldValue / currency.numberToBasic) * newCurrency.numberToBasic);
        setCurrency(newCurrency);
        if (props.onChange) {
            props.onChange(newValue);
        }
        if (props.onCurrencyCodeChange) {
            props.onCurrencyCodeChange(value);
        }
    }, [props.currencyList, props.value, currency, props.onChange, props.onCurrencyCodeChange]);
    useEffect(() => {
        if (props.isLoading) {
            setIsLoadingState(true);
        }
        else {
            setTimeout(() => {
                setIsLoadingState(false);
            }, 200);
        }
    }, [props.isLoading]);
    useEffect(() => {
        if (props.isFocussed ?? isFocussedState) {
            inputRef.current?.focus();
        }
        else {
            inputRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed]);
    useEffect(() => {
        handleCurrencyChange(props?.currencyCode ?? 'USD');
    }, [props.currencyCode]);
    useEffect(() => {
        if (typeof props.value === 'undefined' || props.value === 0) {
            setIsFilledState(false);
            return;
        }
        setIsFilledState(true);
        if (!props.fixedCurrencyCode) {
            setCurrency(props.currencyList.find((entry) => entry.code === props.currencyCode) ?? props.currencyList[0]);
        }
    }, [props.value, props.currencyCode, props.currencyList, props.fixedCurrencyCode]);
    useEffect(() => {
        if (!props.fixedCurrencyCode) {
            return;
        }
        const fixedCurrency = props.currencyList.find((entry) => entry.code === props.fixedCurrencyCode);
        if (!fixedCurrency) {
            return;
        }
        setCurrency(fixedCurrency);
    }, [props.fixedCurrencyCode, props.currencyList]);
    return (React.createElement(InputWrapper, { ref: ref, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, "data-error": typeof props.hasError === 'string' ? props.hasError : undefined, ...styleProps },
        React.createElement(AmountContainer, { ...styleProps },
            React.createElement(InputElement, { value: props.value !== undefined && props.value !== 0 ? Number(props.value) / currency.numberToBasic : '', thousandSeparator: getCurrencyFormat(currency.code, 'thousandSeperator'), decimalScale: currency.decimalDigits, fixedDecimalScale: true, decimalSeparator: getCurrencyFormat(currency.code, 'decimalSeperator') ?? '.', onFocus: (e) => {
                    handleFocusChange(true);
                    onFocus(e);
                }, onBlur: (e) => {
                    handleFocusChange(false);
                    onBlur(e);
                }, onKeyDown: onKeyDown, onKeyPress: handleKeyPress, autoFocus: props.autoFocus, getInputRef: inputRef, type: "text", readOnly: props.readOnly ?? false, maxLength: props.maxLength, "$isLoading": styleProps.isLoading, placeholder: props.placeholder ??
                    `${isCurrencySymbolBefore(currency.code) ? `${currency.symbolNative} ` : ''}0${currency?.decimalDigits && currency.decimalDigits > 0
                        ? `.${new Array(currency.decimalDigits).fill('0').join('')}${!isCurrencySymbolBefore(currency.code) ? ` ${currency.symbolNative}` : ''}`
                        : ''}`, disabled: props.isDisabled ?? false, onValueChange: ({ floatValue }) => handleValueChange(floatValue), [isCurrencySymbolBefore(currency.code) ? 'prefix' : 'suffix']: isCurrencySymbolBefore(currency.code)
                    ? `${currency.symbolNative} `
                    : ` ${currency.symbolNative}` }),
            props.showCurrencyCode &&
                (!props.fixedCurrencyCode ? (React.createElement(StyledDropdown, { options: props.currencyList.map((c) => ({
                        label: `${c.name} (${c.symbol})`,
                        value: c.code,
                    })), active: currency.code, onChange: handleCurrencyChange, onOpenChange: setCurrencyOpen, search: props.currencyList.length > 5, positionRight: true, customLabel: React.createElement(Label, null,
                        currency.code,
                        currencyOpen ? React.createElement(Up, null) : React.createElement(Down, null)), offsetX: styleProps.size === 'small' ? 12 : 16, offsetY: styleProps.size === 'small' ? 4 : 12 })) : (React.createElement(Label, null,
                    currency.code,
                    React.createElement(CurrencyIconWrapper, null, (() => {
                        const CurrencyIconComponent = Icon.Currency[currency.code] || Icon.Currency.USD;
                        return React.createElement(CurrencyIconComponent, null);
                    })()))))),
        props.children,
        isLoadingState && (React.createElement(InputLoadingWrapper, { "$isLoading": styleProps.isLoading },
            React.createElement(InputLoading, { "$isLoading": styleProps.isLoading })))));
});
//# sourceMappingURL=Amount.js.map